import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import useAccess from "../../hooks/useAccess";
import "../../styles/Form.css";
import "../../styles/AdminGroupPage.css";

function AdminGroupPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  //  Access Control
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    groups: {
      create: hasPermission("GroupPage", "Group", "create"),
      edit: hasPermission("GroupPage", "Group", "edit"),
    },
  };

  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/admin-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setGroups(res.data))
      .catch((err) => console.error("Failed to load groups", err));

    axios
      .get(`${BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setUsers(res.data))
      .catch((err) => console.error("Failed to load users", err));
  }, [BASE_URL]);

  const parseEmpGroups = (raw) => {
    if (!raw) return [];
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw.trim()) : raw;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  };

  const getUsersByGroupId = (pg_id) =>
    users.filter((u) => parseEmpGroups(u.emp_groups).includes(Number(pg_id)));

  const filteredGroups = groups.filter((group) => {
    const matchesSearch =
      group.pg_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.pg_email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && group.pg_status === 1) ||
      (statusFilter === "Inactive" && group.pg_status === 0);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="form-wrapper my-3">
      <div className="dashboard-header d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div className="card-body">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="fw-bold pb-2">📋 Group Management</h3>
            {PERMISSIONS.groups.create && (
              <button
                className="btn btn-success dashboard-add-button"
                onClick={() => navigate("/admin-groups/create")}
              >
                ➕ Add Group
              </button>
            )}
          </div>

          {/* Search and Filter */}
          <div className="d-flex gap-2 mb-3 admin-group-filters">
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ maxWidth: "150px" }}
            >
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-hover table-bordered align-middle mb-0 admin-group-table">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th>Name</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th style={{ width: "120px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group, idx) => {
                  const groupUsers = getUsersByGroupId(group.pg_id);
                  return (
                    <tr key={group.pg_id}>
                      <td>{idx + 1}</td>
                      <td className="fw-bold">{group.pg_name}</td>
                      <td>
                        {groupUsers.length > 0 ? (
                          groupUsers.map((u) => u.full_name).join(", ")
                        ) : (
                          <span className="text-muted">No members</span>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        <span
                          className={`status-badge ${
                            group.pg_status === 1 ? "active" : "inactive"
                          }`}
                        >
                          {group.pg_status === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="text-center align-middle">
                        <button
                          className="btn btn-warning btn-sm btn-action"
                          onClick={() =>
                            navigate(`/admin-groups/${group.pg_id}`)
                          }
                          disabled={!PERMISSIONS.groups.edit}
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminGroupPage;
